import React, { useState } from 'react';
import { Users, Phone, Search, BarChart3, TrendingUp, BookOpen, PlayCircle, Award, Calendar, AlertCircle } from 'lucide-react';
import { getStudentVideosProgressByParent, getStudentQuizzesProgressByParent } from '../services/parentProgressService';
import { StudentVideosProgressData, StudentQuizzesProgressData } from '../types/parentProgress';
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const ParentMonitoring: React.FC = () => {
  const [parentPhone, setParentPhone] = useState('');
  const [studentPhone, setStudentPhone] = useState('');
  const [videosData, setVideosData] = useState<StudentVideosProgressData | null>(null);
  const [quizzesData, setQuizzesData] = useState<StudentQuizzesProgressData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  const fetchStudentProgress = async () => {
    if (!parentPhone.trim() || !studentPhone.trim()) {
      setError('يجب إدخال رقم هاتف ولي الأمر ورقم هاتف الطالب');
      return;
    }

    setLoading(true);
    setError(null);
    setHasSearched(true);

    let videosSuccess = false;
    let quizzesSuccess = false;

    // Fetch videos data
    try {
      const videosResponse = await getStudentVideosProgressByParent(parentPhone.trim(), studentPhone.trim());
      if (videosResponse.success && videosResponse.data) {
        setVideosData(videosResponse.data);
        videosSuccess = true;
      } else {
        setVideosData(null);
      }
    } catch (videosError: any) {
      console.error('Error fetching videos data:', videosError);
      setVideosData(null);
    }

    // Fetch quizzes data
    try {
      const quizzesResponse = await getStudentQuizzesProgressByParent(parentPhone.trim(), studentPhone.trim());
      if (quizzesResponse.success && quizzesResponse.data) {
        setQuizzesData(quizzesResponse.data);
        quizzesSuccess = true;
      } else {
        setQuizzesData({ totalQuizzes: 0, solvedQuizzes: 0, results: [] });
      }
    } catch (quizzesError: any) {
      console.error('Error fetching quizzes data:', quizzesError);
      setQuizzesData({ totalQuizzes: 0, solvedQuizzes: 0, results: [] });
    }

    // Only set error if both APIs fail
    if (!videosSuccess && !quizzesSuccess) {
      setError('لم يتم العثور على الطالب بالأرقام المدخلة. تأكد من صحة أرقام الهواتف.');
    }

    setLoading(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchStudentProgress();
  };

  const CircularProgress = ({ percentage, size = 120, strokeWidth = 8, color = '#f97316' }: {
    percentage: number;
    size?: number;
    strokeWidth?: number;
    color?: string;
  }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const safePercentage = isNaN(percentage) ? 0 : Math.max(0, Math.min(100, percentage));
    const strokeDashoffset = circumference - (safePercentage / 100) * circumference;

    return (
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="transform -rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="transparent"
            stroke="#e5e7eb"
            strokeWidth={strokeWidth}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="transparent"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-2xl font-bold text-gray-700">{Math.round(safePercentage)}%</span>
        </div>
      </div>
    );
  };

  const BarChartComponent = ({ data }: { data: Array<{ lessonName: string; score: number; submittedAt: string; lessonId: number }> }) => {
    const maxScore = Math.max(...data.map(item => item.score), 100);
    
    return (
      <div className="space-y-4">
        {data.slice(0, 6).map((item) => (
          <div key={item.lessonId} className="flex items-center space-x-4 space-x-reverse">
            <div className="flex-1">
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-medium text-gray-700 truncate">{item.lessonName}</span>
                <span className="text-sm font-bold text-orange-600">{item.score.toFixed(1)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-gradient-to-r from-orange-400 to-orange-500 h-3 rounded-full transition-all duration-1000 ease-out"
                  style={{ width: `${(item.score / maxScore) * 100}%` }}
                />
              </div>
              <span className="text-xs text-gray-500 mt-1">
                {new Date(item.submittedAt).toLocaleDateString('ar-EG')}
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const videoProgress = videosData && videosData.totalVideos > 0 
    ? (videosData.completedVideos / videosData.totalVideos) * 100 
    : 0;
  const quizProgress = quizzesData && quizzesData.totalQuizzes > 0 
    ? (quizzesData.solvedQuizzes / quizzesData.totalQuizzes) * 100 
    : 0;
  const averageScore = quizzesData?.results && quizzesData.results.length > 0
    ? quizzesData.results.reduce((acc, result) => acc + result.score, 0) / quizzesData.results.length 
    : 0;

  // Prepare data for charts
  const pieChartVideosData = videosData && videosData.totalVideos > 0 ? [
    { name: 'مشاهد', value: videosData.completedVideos },
    { name: 'متبقي', value: videosData.totalVideos - videosData.completedVideos }
  ] : [];

  const pieChartQuizzesData = quizzesData && quizzesData.totalQuizzes > 0 ? [
    { name: 'محلول', value: quizzesData.solvedQuizzes },
    { name: 'متبقي', value: quizzesData.totalQuizzes - quizzesData.solvedQuizzes }
  ] : [];

  const comparisonBarChartData = [
    {
      name: 'الفيديوهات',
      مكتمل: videosData?.completedVideos || 0,
      متبقي: (videosData?.totalVideos || 0) - (videosData?.completedVideos || 0),
    },
    {
      name: 'الاختبارات',
      مكتمل: quizzesData?.solvedQuizzes || 0,
      متبقي: (quizzesData?.totalQuizzes || 0) - (quizzesData?.solvedQuizzes || 0),
    }
  ];

  const quizScoresChartData = quizzesData?.results && Array.isArray(quizzesData.results)
    ? quizzesData.results
        .sort((a, b) => new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime())
        .slice(-10)
        .map(result => ({
          name: result.lessonName.length > 20 
            ? result.lessonName.substring(0, 20) + '...' 
            : result.lessonName,
          score: result.score,
          fullName: result.lessonName
        }))
    : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 pt-20 pb-8" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <Users className="w-12 h-12 text-purple-600 ml-3" />
            <h1 className="text-4xl font-bold text-gray-900">
              متابعة ولي الأمر
            </h1>
          </div>
          <p className="text-lg text-gray-600">
            تابع تقدم طفلك الأكاديمي وأداءه في الاختبارات
          </p>
        </div>

        {/* Search Form */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 border border-purple-100">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6 text-center">
            أدخل بيانات الطالب
          </h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="parentPhone" className="block text-sm font-medium text-gray-700 mb-2">
                  رقم هاتف ولي الأمر
                </label>
                <div className="relative">
                  <Phone className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="tel"
                    id="parentPhone"
                    value={parentPhone}
                    onChange={(e) => setParentPhone(e.target.value)}
                    placeholder="01xxxxxxxxx"
                    className="w-full pr-12 pl-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>
              <div>
                <label htmlFor="studentPhone" className="block text-sm font-medium text-gray-700 mb-2">
                  رقم هاتف الطالب
                </label>
                <div className="relative">
                  <Phone className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="tel"
                    id="studentPhone"
                    value={studentPhone}
                    onChange={(e) => setStudentPhone(e.target.value)}
                    placeholder="01xxxxxxxxx"
                    className="w-full pr-12 pl-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>
            </div>
            <div className="text-center">
              <button
                type="submit"
                disabled={loading}
                className="px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white ml-2"></div>
                    جاري البحث...
                  </div>
                ) : (
                  <div className="flex items-center">
                    <Search className="w-5 h-5 ml-2" />
                    عرض إحصائيات الطالب
                  </div>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg mb-8 flex items-center">
            <AlertCircle className="w-5 h-5 ml-3" />
            <p>{error}</p>
          </div>
        )}

        {/* Results Section */}
        {hasSearched && !loading && !error && (videosData || quizzesData) && (
          <>
            {/* Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {/* Video Progress Card */}
              {videosData && videosData.totalVideos > 0 && (
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-purple-100">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">تقدم المحاضرات</h3>
                      <p className="text-sm text-gray-600">الفيديوهات المشاهدة</p>
                    </div>
                    <PlayCircle className="w-8 h-8 text-blue-500" />
                  </div>
                  <div className="flex items-center justify-center mb-4">
                    <CircularProgress percentage={videoProgress} color="#3b82f6" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600">
                      {videosData.completedVideos || 0} من {videosData.totalVideos || 0} فيديو
                    </p>
                  </div>
                </div>
              )}

              {/* Quiz Progress Card */}
              {quizzesData && quizzesData.totalQuizzes > 0 && (
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-purple-100">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">تقدم الاختبارات</h3>
                      <p className="text-sm text-gray-600">الاختبارات المكتملة</p>
                    </div>
                    <BookOpen className="w-8 h-8 text-green-500" />
                  </div>
                  <div className="flex items-center justify-center mb-4">
                    <CircularProgress percentage={quizProgress} color="#10b981" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600">
                      {quizzesData.solvedQuizzes || 0} من {quizzesData.totalQuizzes || 0} اختبار
                    </p>
                  </div>
                </div>
              )}

              {/* Average Score Card */}
              {quizzesData && quizzesData.results && quizzesData.results.length > 0 && (
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-purple-100">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">متوسط الدرجات</h3>
                      <p className="text-sm text-gray-600">الأداء العام</p>
                    </div>
                    <Award className="w-8 h-8 text-purple-500" />
                  </div>
                  <div className="flex items-center justify-center mb-4">
                    <CircularProgress percentage={averageScore} color="#8b5cf6" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600">
                      {averageScore.toFixed(1)}% متوسط عام
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Statistics Cards */}
            {(videosData || quizzesData) && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                    {videosData && (
                  <>
                    <div className="bg-white rounded-xl shadow-lg p-6 border border-purple-100">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500 mb-1">إجمالي الكورسات</p>
                          <p className="text-2xl font-bold text-gray-900">{videosData.courses?.length || 0}</p>
                        </div>
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                          <BarChart3 className="w-6 h-6 text-blue-600" />
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-lg p-6 border border-purple-100">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500 mb-1">إجمالي الفيديوهات</p>
                          <p className="text-2xl font-bold text-gray-900">{videosData.totalVideos}</p>
                        </div>
                        <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                          <TrendingUp className="w-6 h-6 text-purple-600" />
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-lg p-6 border border-purple-100">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500 mb-1">الفيديوهات المكتملة</p>
                          <p className="text-2xl font-bold text-green-600">{videosData.completedVideos}</p>
                        </div>
                        <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                          <PlayCircle className="w-6 h-6 text-green-600" />
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {quizzesData && quizzesData.totalQuizzes > 0 && (
                  <div className="bg-white rounded-xl shadow-lg p-6 border border-purple-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500 mb-1">إجمالي الاختبارات</p>
                        <p className="text-2xl font-bold text-orange-600">{quizzesData.totalQuizzes}</p>
                      </div>
                      <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                        <BookOpen className="w-6 h-6 text-orange-600" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Progress Comparison Chart */}
            {(videosData && videosData.totalVideos > 0) && (quizzesData && quizzesData.totalQuizzes > 0) && (
              <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-purple-100">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">مقارنة التقدم</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={comparisonBarChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="مكتمل" fill="#10b981" name="مكتمل" />
                    <Bar dataKey="متبقي" fill="#e5e7eb" name="متبقي" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Progress Pie Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* Video Progress Pie Chart */}
              {pieChartVideosData.length > 0 && (
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-purple-100">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">توزيع الفيديوهات</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <RechartsPieChart>
                      <Pie
                        data={pieChartVideosData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${((percent ?? 0) * 100).toFixed(0)}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        <Cell fill="#10b981" />
                        <Cell fill="#e5e7eb" />
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Quiz Progress Pie Chart */}
              {pieChartQuizzesData.length > 0 && (
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-purple-100">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">توزيع الاختبارات</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <RechartsPieChart>
                      <Pie
                        data={pieChartQuizzesData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${((percent ?? 0) * 100).toFixed(0)}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        <Cell fill="#ef4444" />
                        <Cell fill="#e5e7eb" />
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Detailed Statistics */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* Quiz Results Chart */}
              {quizzesData && quizzesData.results && quizzesData.results.length > 0 && (
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-purple-100">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-semibold text-gray-900">نتائج الاختبارات الأخيرة</h3>
                    <BarChart3 className="w-6 h-6 text-purple-500" />
                  </div>
                  <BarChartComponent data={quizzesData.results} />
                </div>
              )}

              {/* Progress Summary */}
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-purple-100">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-gray-900">ملخص التقدم</h3>
                  <TrendingUp className="w-6 h-6 text-green-500" />
                </div>
                <div className="space-y-6">
                  {videosData && (
                    <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                      <div className="flex items-center">
                        <PlayCircle className="w-5 h-5 text-blue-500 ml-3" />
                        <span className="text-sm font-medium text-gray-900">إجمالي الفيديوهات المشاهدة</span>
                      </div>
                      <span className="text-sm font-bold text-blue-600">{videosData.completedVideos || 0}</span>
                    </div>
                  )}
                  
                  {quizzesData && (
                    <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                      <div className="flex items-center">
                        <BookOpen className="w-5 h-5 text-green-500 ml-3" />
                        <span className="text-sm font-medium text-gray-900">إجمالي الاختبارات المكتملة</span>
                      </div>
                      <span className="text-sm font-bold text-green-600">{quizzesData.solvedQuizzes || 0}</span>
                    </div>
                  )}
                  
                  {quizzesData && quizzesData.results && quizzesData.results.length > 0 && (
                    <>
                      <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                        <div className="flex items-center">
                          <Award className="w-5 h-5 text-purple-500 ml-3" />
                          <span className="text-sm font-medium text-gray-900">أعلى درجة</span>
                        </div>
                        <span className="text-sm font-bold text-purple-600">
                          {Math.max(...quizzesData.results.map(r => r.score)).toFixed(1)}%
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg">
                        <div className="flex items-center">
                          <Calendar className="w-5 h-5 text-orange-500 ml-3" />
                          <span className="text-sm font-medium text-gray-900">آخر نشاط</span>
                        </div>
                        <span className="text-sm font-bold text-orange-600">
                          {new Date(Math.max(...quizzesData.results.map(r => new Date(r.submittedAt).getTime()))).toLocaleDateString('ar-EG')}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Quiz Scores Chart */}
            {quizScoresChartData.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-purple-100">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">درجات الاختبارات</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart 
                    data={quizScoresChartData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="name" 
                      angle={-45}
                      textAnchor="end"
                      height={100}
                    />
                    <YAxis domain={[0, 100]} label={{ value: 'الدرجة', angle: -90, position: 'insideLeft' }} />
                    <Tooltip 
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          return (
                            <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
                              <p className="font-semibold">{payload[0].payload.fullName}</p>
                              <p className="text-blue-600">الدرجة: {payload[0].value}%</p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar dataKey="score" fill="#f59e0b" name="الدرجة" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Achievement Badges */}
            {(videoProgress >= 50 || quizProgress >= 50 || averageScore >= 80) && (
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-purple-100">
                <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">الإنجازات المحققة</h3>
                <div className="flex flex-wrap justify-center gap-4">
                  {videoProgress >= 50 && (
                    <div className="flex flex-col items-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <PlayCircle className="w-8 h-8 text-blue-500 mb-2" />
                      <span className="text-sm font-medium text-blue-700">مشاهد نشط</span>
                      <span className="text-xs text-blue-600">50%+ من الفيديوهات</span>
                    </div>
                  )}
                  
                  {quizProgress >= 50 && (
                    <div className="flex flex-col items-center p-4 bg-green-50 rounded-lg border border-green-200">
                      <BookOpen className="w-8 h-8 text-green-500 mb-2" />
                      <span className="text-sm font-medium text-green-700">متقدم في الاختبارات</span>
                      <span className="text-xs text-green-600">50%+ من الاختبارات</span>
                    </div>
                  )}
                  
                  {averageScore >= 80 && (
                    <div className="flex flex-col items-center p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <Award className="w-8 h-8 text-purple-500 mb-2" />
                      <span className="text-sm font-medium text-purple-700">أداء متميز</span>
                      <span className="text-xs text-purple-600">+80% متوسط الدرجات</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* No Results Found */}
        {hasSearched && !loading && !error && !videosData && !quizzesData && (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-purple-100">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">لا توجد بيانات متاحة</h3>
            <p className="text-gray-600">
              تأكد من صحة أرقام الهواتف المدخلة أو تواصل مع الإدارة
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParentMonitoring;
